// ガイドブックの中身。章とページ、そして「どこを説明するか」の指定。
//
// capture はキャプチャのファイル名に使う（builder_guide/{book}_{capture}_{locale}.webp）。
// focus はキャプチャに対する比率（0〜1）。実画面を getBoundingClientRect で実測した値なので、
// 目分量ではなくボタンの位置にぴったり合う。UI を動かしたら測り直すこと。
// キャプチャの撮り直しは scripts/capture-guide.mjs。

import {
  GuideRect,
  GuideBookPage,
  GuideFocusMode,
  GuideBookChapter,
} from "features/builder/elements/BuilderGuideBook";

const KEY = "MSG_BUILDER_GUIDEBOOK";

const full = (capture: string, key: string): GuideBookPage => ({
  key: `${capture}_${key}`,
  capture,
  mode: GuideFocusMode.FULL,
  titleKey: `${KEY}_${key.toUpperCase()}_TITLE`,
  descKey: `${KEY}_${key.toUpperCase()}_DESC`,
});

const spot = (
  capture: string,
  key: string,
  mode: GuideFocusMode,
  focus: GuideRect,
): GuideBookPage => ({
  key: `${capture}_${key}`,
  capture,
  mode,
  focus,
  titleKey: `${KEY}_${key.toUpperCase()}_TITLE`,
  descKey: `${KEY}_${key.toUpperCase()}_DESC`,
});

const ring = (capture: string, key: string, focus: GuideRect) =>
  spot(capture, key, GuideFocusMode.HIGHLIGHT, focus);

const zoom = (capture: string, key: string, focus: GuideRect) =>
  spot(capture, key, GuideFocusMode.ZOOM, focus);

export const TOP_GUIDE_CHAPTERS: GuideBookChapter[] = [
  {
    key: "mine",
    icon: "folder",
    labelKey: `${KEY}_CH_MINE`,
    pages: [
      full("mine", "mine_overview"),
      zoom("mine", "mine_guide", { x: 0.219, y: 0.038, w: 0.349, h: 0.685 }),
      ring("mine", "mine_card", { x: 0.582, y: 0.038, w: 0.349, h: 0.685 }),
      zoom("mine", "mine_stats", { x: 0.589, y: 0.485, w: 0.17, h: 0.095 }),
      zoom("mine", "mine_actions", { x: 0.585, y: 0.57, w: 0.345, h: 0.145 }),
      zoom("mine", "mine_filter", { x: 0.01, y: 0.145, w: 0.19, h: 0.315 }),
      zoom("mine", "mine_sort", { x: 0.01, y: 0.44, w: 0.19, h: 0.43 }),
      ring("mine", "mine_tabs", { x: 0.94, y: 0.03, w: 0.058, h: 0.82 }),
      zoom("mine", "mine_trash", { x: 0.012, y: 0.875, w: 0.175, h: 0.115 }),
    ],
  },
  {
    key: "public",
    icon: "public",
    labelKey: `${KEY}_CH_PUBLIC`,
    pages: [
      full("public", "public_overview"),
      zoom("public", "public_actions", { x: 0.585, y: 0.57, w: 0.345, h: 0.145 }),
      zoom("public", "public_filter", { x: 0.01, y: 0.145, w: 0.19, h: 0.315 }),
      zoom("public", "public_sort", { x: 0.01, y: 0.44, w: 0.19, h: 0.215 }),
    ],
  },
  {
    key: "detail",
    icon: "info",
    labelKey: `${KEY}_CH_DETAIL`,
    pages: [
      full("detail", "detail_overview"),
      zoom("detail", "detail_tabs", { x: 0.355, y: 0.165, w: 0.63, h: 0.115 }),
      zoom("detail", "detail_info", { x: 0.36, y: 0.29, w: 0.62, h: 0.35 }),
      zoom("detail", "detail_reaction", { x: 0.365, y: 0.64, w: 0.51, h: 0.115 }),
      zoom("detail", "detail_expand", { x: 0.81, y: 0.695, w: 0.175, h: 0.11 }),
      zoom("detail", "detail_actions", { x: 0.225, y: 0.83, w: 0.555, h: 0.135 }),
      zoom("detail", "detail_report", { x: 0.805, y: 0.035, w: 0.185, h: 0.105 }),
      full("own", "detail_own"),
      zoom("own", "detail_code", { x: 0.355, y: 0.04, w: 0.21, h: 0.095 }),
    ],
  },
  {
    key: "trash",
    icon: "delete",
    labelKey: `${KEY}_CH_TRASH`,
    pages: [
      zoom("own", "trash_send", { x: 0.82, y: 0.035, w: 0.17, h: 0.105 }),
      full("trash", "trash_overview"),
      zoom("trash", "trash_restore", { x: 0.585, y: 0.57, w: 0.345, h: 0.145 }),
    ],
  },
];

export const EDITOR_GUIDE_CHAPTERS: GuideBookChapter[] = [
  {
    key: "screen",
    icon: "dashboard",
    labelKey: `${KEY}_CH_SCREEN`,
    pages: [
      full("main", "screen_overview"),
      ring("main", "screen_header", { x: 0.22, y: 0.008, w: 0.599, h: 0.142 }),
      ring("main", "screen_steps", { x: 0.005, y: 0.158, w: 0.375, h: 0.621 }),
      ring("main", "screen_tools", { x: 0.911, y: 0.167, w: 0.083, h: 0.817 }),
      ring("main", "screen_assets", { x: 0.241, y: 0.888, w: 0.57, h: 0.104 }),
      zoom("main", "screen_back", { x: 0, y: 0, w: 0.1, h: 0.115 }),
    ],
  },
  {
    key: "build",
    icon: "widgets",
    labelKey: `${KEY}_CH_BUILD`,
    pages: [
      zoom("main", "build_blocks", { x: 0.236, y: 0.882, w: 0.432, h: 0.112 }),
      zoom("main", "build_open", { x: 0.67, y: 0.884, w: 0.068, h: 0.108 }),
      zoom("assets", "build_list", { x: 0.156, y: 0.159, w: 0.688, h: 0.682 }),
      zoom("main", "build_tap", { x: 0.91, y: 0.4, w: 0.088, h: 0.235 }),
      zoom("main", "build_undo", { x: 0.91, y: 0.165, w: 0.088, h: 0.235 }),
      zoom("main", "build_zoom", { x: 0.91, y: 0.638, w: 0.088, h: 0.345 }),
      full("history", "build_history"),
    ],
  },
  {
    key: "setting",
    icon: "tune",
    labelKey: `${KEY}_CH_SETTING`,
    pages: [
      ring("main", "setting_steps", { x: 0.005, y: 0.158, w: 0.375, h: 0.621 }),
      zoom("main", "setting_character", { x: 0.018, y: 0.26, w: 0.354, h: 0.175 }),
      full("commands", "setting_commands"),
      zoom("main", "setting_size", { x: 0.225, y: 0.072, w: 0.1, h: 0.072 }),
      full("ai", "setting_ai"),
      full("info", "setting_info"),
    ],
  },
  {
    key: "publish",
    icon: "rocket_launch",
    labelKey: `${KEY}_CH_PUBLISH`,
    pages: [
      zoom("main", "publish_test", { x: 0.598, y: 0.072, w: 0.115, h: 0.072 }),
      full("test", "publish_play"),
      zoom("test", "publish_commands", { x: 0, y: 0.145, w: 0.26, h: 0.85 }),
      zoom("test", "publish_start", { x: 0.5, y: 0.865, w: 0.24, h: 0.115 }),
      zoom("main", "publish_step", { x: 0.018, y: 0.532, w: 0.354, h: 0.088 }),
      zoom("main", "publish_publish", { x: 0.012, y: 0.704, w: 0.362, h: 0.073 }),
      zoom("main", "publish_help", { x: 0.7, y: 0.072, w: 0.115, h: 0.072 }),
    ],
  },
];
